import { SwitchEvidenceCard } from '@framework/ui/components/configuration-card/SwitchEvidenceCard.tsx';
import { WidgetConfigurationSubCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationSubCard.tsx';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { type ReactNode } from 'react';
import { useWatch } from 'react-hook-form';
import { type IBooleanCondition } from '../../../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';

export interface IDisplayRuleConfigSubCardProps {
  /** Campo condizione osservato per il tag di stato: deve coincidere con il `name` del FormItem nei children. */
  name: 'visible' | 'enabled';
  icon: ReactNode;
  title: string;
  /** Testo del tag con condizione statica attiva (es. "Sempre visibile"). */
  alwaysTagLabel: string;
  /** Testo del tag con condizione statica disattivata (es. "Mai visibile"). */
  neverTagLabel: string;
  /** FormItem + input della condizione, dichiarati dal chiamante. */
  children: ReactNode;
}

/** Condizione assente o vuota equivale ad attiva (vedi conditionResultToBoolean). */
function getConditionStateTag(
  condition: IBooleanCondition | null | undefined,
  alwaysTagLabel: string,
  neverTagLabel: string,
) {
  if (condition?.type === 'expression') {
    return <StyledTag color="blue">In base a regole</StyledTag>;
  }
  if (condition?.type === 'static' && !condition.value) {
    return <StyledTag color="red">{neverTagLabel}</StyledTag>;
  }
  return <StyledTag color="green">{alwaysTagLabel}</StyledTag>;
}

/**
 * Sub-card della card "Regole di visualizzazione" per una singola condizione
 * (Visibilità o Abilitazione): wrapper grafico con tag di stato nell'header,
 * l'input resta nei children del chiamante.
 */
export function DisplayRuleConfigSubCard(
  props: IDisplayRuleConfigSubCardProps,
) {
  const condition = useWatch({ name: props.name }) as
    | IBooleanCondition
    | null
    | undefined;

  return (
    <WidgetConfigurationSubCard
      icon={props.icon}
      title={props.title}
      extra={getConditionStateTag(
        condition,
        props.alwaysTagLabel,
        props.neverTagLabel,
      )}
    >
      <SwitchEvidenceCard removeTopPadding>{props.children}</SwitchEvidenceCard>
    </WidgetConfigurationSubCard>
  );
}
